import React, { useEffect, useState } from "react";

import api from "../../api/client";

import { useSelector, useDispatch } from "react-redux";

import { RootState } from "../../redux/store";

import { setClientId } from "../../redux/userSlice";

import { useNavigate } from "react-router-dom";

import jsPDF from "jspdf";

import autoTable from "jspdf-autotable";

import "./SearchComponent.css";

type MaidStatus = "not_arrived" | "arrived";

type SearchRow = {
  client_id: number;
  client_name: string;
  contact_info: string;
  maid_id: number | null;
  maid_name: string | null;
  agent_name: string | null;
  status: MaidStatus | null;
  arrival_date: string | null;
};

type SavedSearchState = {
  searchTerm: string;
  searchType: string;
  results: SearchRow[];
  message: string;
};

const SEARCH_STORAGE_KEY = "baytcare_search_state";

const SearchComponent: React.FC = () => {
  const token = useSelector((state: RootState) => state.user.token);

  const dispatch = useDispatch();

  const navigate = useNavigate();

  const [searchTerm, setSearchTerm] = useState("");

  const [searchType, setSearchType] = useState("client_name");

  const [results, setResults] = useState<SearchRow[]>([]);

  const [message, setMessage] = useState("");

  const [searching, setSearching] = useState(false);

  /* ============================================================
     RESTORE LAST SEARCH
  ============================================================ */

  useEffect(() => {
    try {
      const savedSearch = sessionStorage.getItem(SEARCH_STORAGE_KEY);

      if (!savedSearch) {
        return;
      }

      const parsedSearch: SavedSearchState = JSON.parse(savedSearch);

      setSearchTerm(parsedSearch.searchTerm || "");

      setSearchType(parsedSearch.searchType || "client_name");

      setResults(
        Array.isArray(parsedSearch.results) ? parsedSearch.results : []
      );

      setMessage(parsedSearch.message || "");
    } catch (error) {
      console.error("Unable to restore previous search:", error);

      sessionStorage.removeItem(SEARCH_STORAGE_KEY);
    }
  }, []);

  /* ============================================================
     SAVE SEARCH STATE
  ============================================================ */

  const saveSearchState = (
    currentSearchTerm: string,
    currentSearchType: string,
    currentResults: SearchRow[],
    currentMessage: string
  ) => {
    const searchState: SavedSearchState = {
      searchTerm: currentSearchTerm,
      searchType: currentSearchType,
      results: currentResults,
      message: currentMessage,
    };

    sessionStorage.setItem(SEARCH_STORAGE_KEY, JSON.stringify(searchState));
  };

  /* ============================================================
     SEARCH
  ============================================================ */

  const handleSearch = async () => {
    if (!token) {
      setMessage("Your session has expired. Please sign in again.");

      return;
    }

    try {
      setSearching(true);

      setMessage("");

      const response = await api.get("/search", {
        params: {
          field: searchType,
          query: searchTerm.trim(),
        },

        headers: {
          Authorization: `Bearer ${token}`,
        },
      });

      const searchResults = (response.data.results || []) as SearchRow[];

      /* ============================================================
         SORT BY ARRIVAL DATE

         Oldest arrival date = top
         Newest arrival date = bottom
         No arrival date = very bottom
      ============================================================ */

      const sortedResults = [...searchResults].sort((a, b) => {
        // Both have no arrival date.
        if (!a.arrival_date && !b.arrival_date) {
          return 0;
        }

        // A has no arrival date -> move A to bottom.
        if (!a.arrival_date) {
          return 1;
        }

        // B has no arrival date -> move B to bottom.
        if (!b.arrival_date) {
          return -1;
        }

        // Oldest date first.
        return (
          new Date(a.arrival_date).getTime() -
          new Date(b.arrival_date).getTime()
        );
      });

      setResults(sortedResults);

      let resultMessage = "";

      if (sortedResults.length === 0) {
        resultMessage = "No results found.";
      } else {
        resultMessage = `${sortedResults.length} ${
          sortedResults.length === 1 ? "result" : "results"
        } found.`;
      }

      setMessage(resultMessage);

      /* ============================================================
         REMEMBER THIS SEARCH
      ============================================================ */

      saveSearchState(searchTerm, searchType, sortedResults, resultMessage);
    } catch (error: any) {
      setResults([]);

      const errorMessage =
        error?.response?.data?.message ?? "Unexpected error during search.";

      setMessage(errorMessage);
    } finally {
      setSearching(false);
    }
  };

  /* ============================================================
     OPEN CLIENT
  ============================================================ */

  const handleRowClick = (clientId: number) => {
    /*
     * Save the current table again immediately before leaving.
     *
     * This guarantees that when the user presses Back,
     * the exact same search table can be restored.
     */

    saveSearchState(searchTerm, searchType, results, message);

    dispatch(setClientId(clientId));

    navigate(`/edit-client/${clientId}`);
  };

  /* ============================================================
     CHANGE SEARCH TYPE
  ============================================================ */

  const handleSearchTypeChange = (value: string) => {
    setSearchType(value);

    setSearchTerm("");

    setResults([]);

    setMessage("");

    /*
     * The user has started a new search,
     * so remove the previous saved table.
     */

    sessionStorage.removeItem(SEARCH_STORAGE_KEY);
  };

  /* ============================================================
     SEARCH TERM CHANGE
  ============================================================ */

  const handleSearchTermChange = (value: string) => {
    setSearchTerm(value);

    /*
     * We do NOT delete the saved search here.
     *
     * The previous search remains stored until the user
     * actually performs another search.
     */
  };

  /* ============================================================
     DISPLAY STATUS
  ============================================================ */

  const displayStatus = (status: MaidStatus | null) => {
    if (status === "arrived") {
      return "Arrived";
    }

    if (status === "not_arrived") {
      return "Have Not Arrived";
    }

    return "—";
  };

  /* ============================================================
     EXPORT PDF
  ============================================================ */

  const exportToPDF = () => {
    if (results.length === 0) {
      return;
    }

    const doc = new jsPDF();

    doc.setFontSize(18);

    doc.text("Search Results", 14, 20);

    const header = [
      "No.",
      "Client Name",
      "Contact Info",
      "Maid Name",
      "Agent",
      "Arrival Status",
      "Arrival Date",
    ];

    /*
     * Uses the same sorted results shown on screen.
     */

    const rows = results.map((r, index) => [
      index + 1,
      r.client_name,
      r.contact_info,
      r.maid_name || "—",
      r.agent_name || "—",
      displayStatus(r.status),
      r.arrival_date || "—",
    ]);

    autoTable(doc, {
      head: [header],

      body: rows,

      startY: 30,

      theme: "striped",

      headStyles: {
        fillColor: [0, 123, 255],

        textColor: [255, 255, 255],
      },
    });

    doc.save("search_results.pdf");
  };

  /* ============================================================
     PAGE
  ============================================================ */

  return (
    <div className="search-component">
      <h2>Search Clients or Maids</h2>

      <div className="search-form">
        <select
          value={searchType}
          onChange={(e) => handleSearchTypeChange(e.target.value)}
        >
          <option value="client_name">Client Name</option>

          <option value="contact_info">Contact Info</option>

          <option value="maid_name">Maid Name</option>

          <option value="arrival_date">Arrival Date</option>

          <option value="status">Arrival Status</option>
        </select>

        {searchType === "status" ? (
          <select
            value={searchTerm}
            onChange={(e) => handleSearchTermChange(e.target.value)}
          >
            <option value="">All Statuses</option>

            <option value="arrived">Arrived</option>

            <option value="not_arrived">Have Not Arrived</option>
          </select>
        ) : (
          <input
            type={searchType === "arrival_date" ? "date" : "text"}
            placeholder="Leave empty to show all"
            value={searchTerm}
            onChange={(e) => handleSearchTermChange(e.target.value)}
            onKeyDown={(e) => {
              if (e.key === "Enter") {
                handleSearch();
              }
            }}
          />
        )}

        <button onClick={handleSearch} disabled={searching}>
          {searching ? "Loading..." : "Search"}
        </button>
      </div>

      {message && <p className="message">{message}</p>}

      {results.length > 0 && (
        <div className="results-section">
          <h3>Search Results</h3>

          <table className="results-table">
            <thead>
              <tr>
                <th>No.</th>

                <th>Client</th>

                <th>Contact Info</th>

                <th>Maid</th>

                <th>Agent</th>

                <th>Arrival Status</th>

                <th>Arrival Date</th>
              </tr>
            </thead>

            <tbody>
              {results.map((item, index) => (
                <tr
                  key={
                    item.maid_id !== null
                      ? `${item.client_id}-${item.maid_id}`
                      : `${item.client_id}-${index}`
                  }
                  onClick={() => handleRowClick(item.client_id)}
                  style={{
                    cursor: "pointer",
                  }}
                >
                  <td>{index + 1}</td>

                  <td>{item.client_name}</td>

                  <td>{item.contact_info}</td>

                  <td>{item.maid_name || "—"}</td>

                  <td>{item.agent_name || "—"}</td>

                  <td>
                    <span
                      className={
                        item.status === "arrived"
                          ? "status-badge status-arrived"
                          : item.status === "not_arrived"
                          ? "status-badge status-not-arrived"
                          : "status-badge"
                      }
                    >
                      {displayStatus(item.status)}
                    </span>
                  </td>

                  <td>{item.arrival_date || "—"}</td>
                </tr>
              ))}
            </tbody>
          </table>

          <button onClick={exportToPDF} className="export-btn">
            Export to PDF
          </button>
        </div>
      )}
    </div>
  );
};

export default SearchComponent;
